import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, Check, X, ShoppingBag, ShieldCheck, Search, Sparkles } from "lucide-react";
import { checkDomain } from "../../lib/api";

const EASE = [0.16, 1, 0.3, 1];
const TLDS = [".sa", ".com.sa", ".org.sa", ".net.sa", ".gov.sa", ".edu.sa"];

/**
 * HeroB — Saudi Modern Maximalist
 * Dark redsea-grain canvas with geometric Asir-inspired diamond pattern overlay,
 * glass-morphism search shell, large white display headline, souq-yellow accents.
 */
export const HeroB = ({ onAddToCart }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [activeTLD, setActiveTLD] = useState(".sa");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const submit = async (e) => {
        e?.preventDefault?.();
        const q = query.trim();
        if (!q) return;
        setLoading(true); setError(""); setResult(null);
        try {
            const data = await checkDomain(q);
            setResult(data);
        } catch (err) {
            setError(
                err?.response?.data?.detail === "invalid_domain_label"
                    ? "Try a clean label, e.g. mybrand"
                    : "Could not check availability — try again.",
            );
        } finally { setLoading(false); }
    };

    return (
        <section
            id="hero-search"
            className="relative isolate overflow-hidden bg-redsea-700 pt-[110px] lg:pt-[140px]"
            data-testid="hero-b"
        >
            {/* Diamond geometric pattern overlay (Asir-inspired) */}
            <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.07]" aria-hidden="true">
                <defs>
                    <pattern id="diamonds-b" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M20 0 L40 20 L20 40 L0 20 Z" fill="none" stroke="#E59500" strokeWidth="0.7" />
                        <circle cx="20" cy="20" r="2" fill="#E59500" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#diamonds-b)" />
            </svg>

            {/* Souq corner glow */}
            <div className="pointer-events-none absolute -top-40 -end-40 h-[520px] w-[520px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(229,149,0,0.22) 0%, transparent 65%)" }}
                aria-hidden="true"
            />
            {/* Madder edge accent */}
            <div className="pointer-events-none absolute -bottom-32 -start-20 h-[400px] w-[400px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(138,30,18,0.30) 0%, transparent 70%)" }}
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-[1320px] px-5 pb-24 lg:px-8 lg:pb-32">
                <div className="grid grid-cols-12 items-center gap-6 lg:gap-12">
                    {/* LEFT: content (7/12) */}
                    <div className="col-span-12 lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: EASE }}
                            className="inline-flex items-center gap-2 rounded-full border-2 border-souq-400/60 bg-white/5 px-4 py-2 backdrop-blur-md"
                            data-testid="hero-b-kicker"
                        >
                            <Sparkles className="h-4 w-4 text-souq-400" />
                            <span className="text-[14px] font-bold uppercase tracking-[0.16em] text-souq-300">
                                {t("home.heroKicker")}
                            </span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
                            className="display-headline mt-7 max-w-[14ch]"
                            style={{ color: "#FFFFFF" }}
                            data-testid="hero-b-title"
                        >
                            {t("home.heroQuestion1")}{" "}
                            <span style={{ color: "#E59500" }}>{t("home.heroQuestionAccent")}</span>{" "}
                            {t("home.heroQuestion2")}
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.12 }}
                            className="lead mt-7 max-w-[58ch] text-redsea-100"
                            style={{ color: "#C8E0E3" }}
                        >
                            {t("home.heroSubtitle")}
                        </motion.p>

                        {/* Glass-morphism search */}
                        <motion.form
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: EASE, delay: 0.2 }}
                            onSubmit={submit}
                            className="mt-9 max-w-[720px]"
                            data-testid="hero-b-search-form"
                        >
                            <div className="flex items-center gap-2 rounded-[14px] border-2 border-souq-400/50 bg-white/8 px-3 py-2 backdrop-blur-xl transition-colors duration-200 focus-within:border-souq-400 focus-within:bg-white/12"
                                style={{ height: "76px" }}
                            >
                                <span className="ps-3 text-souq-300">
                                    <Search className="h-6 w-6" strokeWidth={2.4} />
                                </span>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={t("home.heroSearchPlaceholder")}
                                    aria-label={t("home.heroSearchPlaceholder")}
                                    className="h-full flex-1 bg-transparent px-2 text-[20px] font-medium text-white placeholder:text-redsea-200 focus:outline-none"
                                    data-testid="hero-b-search-input"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || !query.trim()}
                                    data-testid="hero-b-search-submit"
                                    className="inline-flex h-[60px] min-w-[160px] items-center justify-center gap-2 rounded-[10px] bg-souq-400 px-6 text-[17px] font-bold text-ink-900 shadow-souq transition-all duration-200 hover:bg-souq-300 active:translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {loading ? (
                                        <><Loader2 className="h-5 w-5 animate-spin" />{t("cta.checking")}</>
                                    ) : (
                                        <>{t("cta.search")}<ArrowRight className="h-5 w-5 rtl-mirror" /></>
                                    )}
                                </button>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-2">
                                {TLDS.map((tld) => {
                                    const active = activeTLD === tld;
                                    return (
                                        <button
                                            type="button"
                                            key={tld}
                                            onClick={() => {
                                                setActiveTLD(tld);
                                                const base = (query.trim() || "").replace(/\.(sa|com\.sa|org\.sa|net\.sa|gov\.sa|edu\.sa|com|net)$/i, "");
                                                setQuery((base || "mybrand") + tld);
                                                setTimeout(() => inputRef.current?.focus(), 0);
                                            }}
                                            data-testid={`hero-b-tld-${tld.replace(/\./g, "")}`}
                                            className={`inline-flex h-12 min-h-[48px] items-center rounded-[8px] border-2 px-4 font-mono text-[15px] font-semibold transition-all duration-200 ease-nour num-ltr ${
                                                active
                                                    ? "border-souq-400 bg-souq-400 text-ink-900"
                                                    : "border-white/30 bg-white/5 text-white hover:border-souq-400 hover:bg-white/10"
                                            }`}
                                        >
                                            {tld}
                                        </button>
                                    );
                                })}
                            </div>

                            <p className="mt-5 text-[16px] font-medium text-redsea-200">
                                {t("home.heroSearchHint")} <span className="text-redsea-300">·</span>{" "}
                                <span className="num-ltr tabular-nums text-white">{t("home.heroFootnote")}</span>
                            </p>
                        </motion.form>

                        {/* Result panel */}
                        <AnimatePresence mode="wait">
                            {(loading || result || error) && (
                                <motion.div
                                    key={loading ? "load" : result ? "ok" : "err"}
                                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.24, ease: EASE }}
                                    className="mt-7 max-w-[720px]"
                                    data-testid="hero-b-result"
                                >
                                    {loading && (
                                        <div className="rounded-card border-2 border-white/20 bg-white/5 p-6 backdrop-blur-md">
                                            <div className="h-4 w-44 animate-pulse rounded-full bg-white/10" />
                                            <div className="mt-4 h-4 w-full animate-pulse rounded-full bg-white/10" />
                                        </div>
                                    )}
                                    {error && !loading && (
                                        <div className="rounded-card border-2 border-madder-400 bg-madder-500/30 p-5 text-[16px] font-semibold text-madder-100 backdrop-blur-md">{error}</div>
                                    )}
                                    {result && !loading && (
                                        <div className="rounded-card border-2 border-souq-400 bg-white/8 p-7 backdrop-blur-xl">
                                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                                <div className="flex items-center gap-4">
                                                    <span className={`inline-flex h-14 w-14 items-center justify-center rounded-[10px] ${result.primary.available ? "bg-souq-400 text-ink-900" : "bg-white/10 text-redsea-200"}`}>
                                                        {result.primary.available ? <Check className="h-7 w-7" strokeWidth={3} /> : <X className="h-7 w-7" strokeWidth={3} />}
                                                    </span>
                                                    <div>
                                                        <p className="font-mono text-[20px] font-bold text-white num-ltr">{result.primary.domain}</p>
                                                        <p className="text-[16px] font-semibold mt-1">
                                                            {result.primary.available ? <span className="text-souq-300">{t("domainResult.available")}</span> : <span className="text-redsea-200">{t("domainResult.taken")}</span>}
                                                            {result.primary.available && (
                                                                <> <span className="text-redsea-300">·</span> <span className="num-ltr tabular-nums text-white">{result.primary.price_sar} {t("domainResult.perYear")}</span></>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                {result.primary.available && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onAddToCart?.(result.primary)}
                                                        data-testid="hero-b-add-to-cart"
                                                        className="inline-flex h-[56px] items-center gap-2 rounded-btn bg-madder-500 px-6 text-[16px] font-bold text-white transition-all duration-200 hover:bg-madder-600"
                                                    >
                                                        <ShoppingBag className="h-5 w-5" />
                                                        {t("domainResult.addToCart")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* RIGHT: dense glass stack (5/12) */}
                    <motion.aside
                        initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, ease: EASE, delay: 0.25 }}
                        className="hidden lg:flex lg:col-span-5 flex-col gap-4"
                    >
                        {/* Dense triple-stat glass card */}
                        <div className="rounded-card border-2 border-white/15 bg-white/5 p-7 backdrop-blur-xl">
                            <div className="flex items-center gap-2 text-souq-300">
                                <ShieldCheck className="h-5 w-5" />
                                <p className="text-[12px] font-bold uppercase tracking-[0.18em]">SaudiNIC · ZATCA · PDPL</p>
                            </div>
                            <div className="mt-6 grid grid-cols-3 gap-4">
                                <div>
                                    <p className="font-display text-[36px] font-extrabold leading-none tabular-nums num-ltr text-white">240K+</p>
                                    <p className="mt-2 text-[13px] font-semibold text-redsea-100">domains</p>
                                </div>
                                <div>
                                    <p className="font-display text-[36px] font-extrabold leading-none tabular-nums num-ltr text-souq-400">99.99</p>
                                    <p className="mt-2 text-[13px] font-semibold text-redsea-100">% uptime</p>
                                </div>
                                <div>
                                    <p className="font-display text-[36px] font-extrabold leading-none tabular-nums num-ltr text-white">24/7</p>
                                    <p className="mt-2 text-[13px] font-semibold text-redsea-100">Riyadh</p>
                                </div>
                            </div>
                        </div>

                        {/* DNS console preview — glass dark */}
                        <div className="rounded-card border-2 border-white/15 bg-black/40 p-5 backdrop-blur-xl font-mono text-[13px] text-white num-ltr">
                            <div className="flex items-center gap-2 pb-3 border-b border-white/10">
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                                <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                                <span className="ms-2 text-souq-300 text-[12px] font-semibold">nour.net / dns</span>
                            </div>
                            {[
                                { t: "A", v: "185.199.108.153", c: "text-souq-400" },
                                { t: "MX", v: "10 mail.nour.net", c: "text-souq-400" },
                                { t: "TXT", v: "v=spf1 ~all", c: "text-souq-400" },
                                { t: "TXT", v: "v=DMARC1; p=quarantine", c: "text-souq-400" },
                            ].map((r, i) => (
                                <div key={i} className="flex items-center gap-3 pt-2.5">
                                    <span className={`w-12 font-bold ${r.c}`}>{r.t}</span>
                                    <span className="truncate text-white">{r.v}</span>
                                </div>
                            ))}
                            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-souq-400 text-[12px] font-bold">
                                <span className="h-2 w-2 rounded-full bg-souq-400" style={{ animation: "pulse-soft 1.4s ease-in-out infinite" }} />
                                LIVE · propagated 8s
                            </div>
                        </div>

                        {/* Madder badge */}
                        <div className="rounded-card bg-madder-500 p-5 text-white shadow-madder">
                            <p className="text-[13px] font-bold uppercase tracking-[0.18em] text-souq-300">Saudi-hosted</p>
                            <p className="mt-2 font-display text-[24px] font-bold leading-tight">Data residency inside the Kingdom.</p>
                        </div>
                    </motion.aside>
                </div>
            </div>
        </section>
    );
};
